import { useEffect, useCallback, useMemo, useState } from 'react';
import { useInactiveTeamListItemBuilder } from './useInactiveTeamListItemBuilder';
import { getTableConfig } from './tableConfig';
import useMuiTableBuilderV2 from 'hooks/useMuiTableBuilderV2';
import { Box } from '@mui/material';
import { FetchActiveTeamsSuccessResponse } from 'ReportsModule/types';
import TablePagination from '@mui/material/TablePagination';
import { useTablePagination } from 'hooks/useTablePagination';
import useTeams from 'hooks/useTeams';

const tableConfig = getTableConfig();

export const InactiveTeamsTable = ({
  inactiveTeamIds,
  isLoading,
}: {
  inactiveTeamIds: number[];
  isLoading: boolean;
}) => {
  const { debouncedFetchTeams, fetchedLocalTeamIdsSet } = useTeams();
  const listItemsBuilder = useInactiveTeamListItemBuilder();
  const { startIndex, endIndex, pageNumber, handlePageChange, limit } = useTablePagination();

  const teamIdsToRender = useMemo(() => {
    // Show newest team first
    const sortedIds = [...inactiveTeamIds].sort(function (a, b) {
      return b - a;
    });

    return sortedIds.slice(startIndex, endIndex);
  }, [endIndex, inactiveTeamIds, startIndex]);

  useEffect(() => {
    const idsToFetch = teamIdsToRender.filter((id) => !fetchedLocalTeamIdsSet.has(id));
    if (idsToFetch.length) {
      debouncedFetchTeams({ teamIds: idsToFetch, skipCsmFetch: true });
    }
  }, [teamIdsToRender, debouncedFetchTeams, fetchedLocalTeamIdsSet]);

  const mainList = useMemo(() => {
    return {
      listItems: listItemsBuilder({ teamIds: teamIdsToRender }),
    };
  }, [listItemsBuilder, teamIdsToRender]);

  const onPageChange = useCallback(
    (event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null, page: number) => {
      handlePageChange({ newPageNumber: page });
    },
    [handlePageChange],
  );
  const renderFooterOptions = useCallback(() => {
    return (
      <TablePagination
        component="div"
        count={inactiveTeamIds.length}
        page={pageNumber}
        onPageChange={onPageChange}
        color="primary"
        rowsPerPage={limit}
        rowsPerPageOptions={[]}
      />
    );
  }, [inactiveTeamIds.length, limit, onPageChange, pageNumber]);

  const { TableComponent } = useMuiTableBuilderV2({
    ...tableConfig,
    mainList,
    isLoading: isLoading,
    tableMaxHeight: '800px',
    tableHeaderTitle: 'Inactive Teams',
    hideToolbar: false,
    renderFooterOptions,
  });

  return <Box>{TableComponent}</Box>;
};
